import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MapPin, Building2, Plane, Bus, CarTaxiFront, TrainFront, Footprints, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Spinner } from "../components/common";
import { SITE } from "../lib/site";
import { RouteCard } from "./Rail";
import { useAuth } from "../context/AuthContext";
import DiscussionBoard from "../components/DiscussionBoard";
import AskCommunityCTA from "../components/AskCommunityCTA";
import api from "../lib/api";

const CONN_ICONS = {
  metro: <TrainFront className="w-3.5 h-3.5" />, subway: <TrainFront className="w-3.5 h-3.5" />,
  commuter_rail: <TrainFront className="w-3.5 h-3.5" />, bus: <Bus className="w-3.5 h-3.5" />,
  taxi: <CarTaxiFront className="w-3.5 h-3.5" />, rideshare: <CarTaxiFront className="w-3.5 h-3.5" />,
  walk: <Footprints className="w-3.5 h-3.5" />,
};

export default function RailStation() {
  const { id } = useParams();
  const [station, setStation] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    setStation(null);
    api.get(`/rail/stations/${id}`).then((r) => setStation(r.data)).catch(() => setError(true));
  }, [id]);

  if (error) return <div className="max-w-4xl mx-auto p-10 text-center text-muted-foreground">Station not found. <Link to="/rail" className="text-primary underline">Back to Rail</Link></div>;
  if (!station) return <Spinner full />;

  return (
    <div data-testid={`rail-station-page-${station.id}`}>
      <PageHeader eyebrow={`Rail Station · ${station.country}`} title={station.name}
        subtitle={`${station.city}, ${station.country}${station.local_name && station.local_name !== station.name ? ` · ${station.local_name}` : ""}`}
        entityCode={station.code || "RAIL"} />
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          <section className="border border-zinc-300 bg-white p-6">
            <h2 className="font-heading font-900 text-xl mb-4">Quick information</h2>
            <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <Info label="City" value={`${station.city}, ${station.country}`} />
              <Info label="Station code" value={station.code || "—"} />
              <Info label="Coordinates" value={`${station.lat?.toFixed(4)}, ${station.lon?.toFixed(4)}`} />
              <Info label="Platforms" value={station.platforms ?? "Information unavailable"} />
              <Info label="Accessibility" value={station.accessibility_status === "unknown" ? "Requires verification" : station.accessibility_status} />
              <Info label="Status" value={station.status} />
            </dl>
            <div className="mt-4 flex flex-wrap gap-3 text-xs">
              <a href={`https://www.openstreetmap.org/?mlat=${station.lat}&mlon=${station.lon}#map=16/${station.lat}/${station.lon}`}
                target="_blank" rel="noreferrer" data-testid="station-map-link"
                className="inline-flex items-center gap-1 text-emerald-700 font-700 hover:underline"><MapPin className="w-3.5 h-3.5" /> View on map ↗</a>
              <span className="text-muted-foreground">Last verified {station.last_verified} · Confidence {station.confidence}</span>
            </div>
          </section>

          {station.routes?.length > 0 && (
            <section>
              <h2 className="font-heading font-900 text-xl mb-4">Services calling here</h2>
              <div className="grid sm:grid-cols-2 gap-4">{station.routes.map((r) => <RouteCard key={r.id} r={r} />)}</div>
            </section>
          )}

          {station.major_destinations?.length > 0 && (
            <section className="border border-zinc-300 bg-white p-6">
              <h2 className="font-heading font-900 text-xl mb-3">Major destinations</h2>
              <div className="flex flex-wrap gap-2">
                {station.major_destinations.map((d) => (
                  <span key={d} className="border border-emerald-200 bg-emerald-50 text-emerald-900 px-3 py-1 text-sm">{d}</span>
                ))}
              </div>
            </section>
          )}

          <section id="discussion" className="scroll-mt-24 space-y-5" data-testid="station-discussion">
            <AskCommunityCTA entityName={station.name} targetId="discussion" />
            <DiscussionBoard entityType="rail_station" entityId={station.id} entityName={station.name}
              placeholder={`Delays, platform tips, luggage storage, accessibility — share or ask about ${station.name}…`} />
          </section>
        </div>

        <aside className="space-y-6">
          <section className="border border-zinc-300 bg-white p-5">
            <h3 className="font-heading font-700 text-lg mb-3 flex items-center gap-2"><Building2 className="w-4 h-4 text-emerald-700" /> Operators</h3>
            {(station.operator_details || []).map((o) => (
              <Link key={o.id} to={`/rail/operators/${o.id}`} className="block py-2 border-b border-zinc-100 last:border-0 text-sm hover:text-emerald-700">
                <span className="font-600">{o.common_name}</span>
                <span className="text-muted-foreground"> · {o.country}</span>
              </Link>
            ))}
          </section>

          <section className="border border-zinc-300 bg-white p-5" data-testid="station-connections">
            <h3 className="font-heading font-700 text-lg mb-3">Transportation connections</h3>
            <div className="flex flex-wrap gap-2">
              {(station.connections || []).map((c) => (
                <span key={c} className="inline-flex items-center gap-1.5 border border-zinc-300 px-2.5 py-1 text-xs capitalize">{CONN_ICONS[c] || <TrainFront className="w-3.5 h-3.5" />}{c.replace("_", " ")}</span>
              ))}
            </div>
            {station.nearby_airport && (
              <Link to={`/airports?q=${station.nearby_airport}`} data-testid="station-airport-link"
                className="mt-3 flex items-center gap-2 border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-900 hover:border-sky-400 transition-colors">
                <Plane className="w-4 h-4" /> Nearest major airport: <span className="font-mono font-700">{station.nearby_airport}</span>
              </Link>
            )}
          </section>

          <ReportUpdate entityType="station" entityId={station.id} />

          <Link to="/rail" className="block text-center border border-zinc-300 bg-white px-4 py-2.5 text-sm font-700 hover:border-emerald-600 transition-colors">
            ← All rail stations
          </Link>
        </aside>
      </div>
    </div>
  );
}

const Info = ({ label, value }) => (
  <div><dt className="text-xs font-mono uppercase tracking-wide text-muted-foreground">{label}</dt><dd className="font-600 capitalize">{value}</dd></div>
);

const ISSUES = [
  ["incorrect_information", "Incorrect information"],
  ["facility_unavailable", "Facility unavailable"],
  ["accessibility_problem", "Accessibility problem"],
  ["schedule_changed", "Schedule changed"],
  ["new_service", "New service"],
  ["service_discontinued", "Service discontinued"],
  ["temporary_closure", "Temporary closure"],
  ["station_closed", "Station closed"],
  ["name_changed", "Name changed"],
  ["other", "Other"],
];

export function ReportUpdate({ entityType, entityId, mode }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [issue, setIssue] = useState("incorrect_information");
  const [detail, setDetail] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (detail.trim().length < 5) { toast.error("Please describe the update (at least a few words)."); return; }
    setBusy(true);
    try {
      if (mode) {
        await api.post("/transport/reports", { mode, entity_type: entityType, entity_id: entityId, issue_type: issue, detail });
      } else {
        await api.post("/rail/reports", { entity_type: entityType, entity_id: entityId, issue_type: issue, detail });
      }
      toast.success("Thank you — your report has entered the verification queue.");
      setOpen(false); setDetail("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit the report.");
    } finally { setBusy(false); }
  };

  return (
    <section className="border border-amber-300 bg-amber-50 p-5" data-testid="report-update">
      <h3 className="font-heading font-700 text-base mb-1 flex items-center gap-2 text-amber-900"><AlertTriangle className="w-4 h-4" /> Report an update</h3>
      <p className="text-xs text-amber-800 mb-3">Closed station? Changed name? Facility out of service? Help keep {SITE.name} accurate.</p>
      {!user ? (
        <Link to="/login" className="text-sm font-700 text-amber-900 underline" data-testid="report-login-link">Sign in to report an update</Link>
      ) : !open ? (
        <button onClick={() => setOpen(true)} data-testid="report-open-btn"
          className="border border-amber-500 bg-white px-3 py-1.5 text-sm font-700 text-amber-900 hover:bg-amber-100 transition-colors">Report an update</button>
      ) : (
        <div className="space-y-2">
          <select value={issue} onChange={(e) => setIssue(e.target.value)} data-testid="report-issue-select"
            className="w-full border border-amber-300 bg-white px-2 py-1.5 text-sm">
            {ISSUES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <textarea value={detail} onChange={(e) => setDetail(e.target.value)} rows={3} data-testid="report-detail-input"
            placeholder="What changed? Include a source link if you have one."
            className="w-full border border-amber-300 bg-white px-2 py-1.5 text-sm" />
          <div className="flex gap-2">
            <button onClick={submit} disabled={busy} data-testid="report-submit-btn"
              className="border border-amber-600 bg-amber-600 text-white px-3 py-1.5 text-sm font-700 hover:bg-amber-700 disabled:opacity-60 transition-colors">{busy ? "Sending…" : "Submit report"}</button>
            <button onClick={() => setOpen(false)} className="text-sm text-amber-800 underline">Cancel</button>
          </div>
        </div>
      )}
    </section>
  );
}
